import type { CampaignCalendar } from '@roll1/shared';

/** Harptos Calendar — Forgotten Realms (D&D 5e default setting) */
export const HARPTOS_CALENDAR: CampaignCalendar = {
  name: 'Harptos (Forgotten Realms)',
  months: [
    { name: 'Hammer', days: 30 },
    { name: 'Midwinter', days: 1, intercalary: true },
    { name: 'Alturiak', days: 30 },
    { name: 'Ches', days: 30 },
    { name: 'Tarsakh', days: 30 },
    { name: 'Greengrass', days: 1, intercalary: true },
    { name: 'Mirtul', days: 30 },
    { name: 'Kythorn', days: 30 },
    { name: 'Flamerule', days: 30 },
    { name: 'Midsummer', days: 1, intercalary: true },
    { name: 'Eleasis', days: 30 },
    { name: 'Eleint', days: 30 },
    { name: 'Highharvestide', days: 1, intercalary: true },
    { name: 'Marpenoth', days: 30 },
    { name: 'Uktar', days: 30 },
    { name: 'Feast of the Moon', days: 1, intercalary: true },
    { name: 'Nightal', days: 30 },
  ],
  daysPerWeek: 10,
  weekDayNames: [
    'First-day', 'Second-day', 'Third-day', 'Fourth-day', 'Fifth-day',
    'Sixth-day', 'Seventh-day', 'Eighth-day', 'Ninth-day', 'Tenth-day',
  ],
  yearLabel: 'DR',
  epoch: 'Dalereckoning',
};

/** Galifar Calendar — Eberron */
export const GALIFAR_CALENDAR: CampaignCalendar = {
  name: 'Galifar (Eberron)',
  months: [
    { name: 'Zarantyr', days: 28 },
    { name: 'Olarune', days: 28 },
    { name: 'Therendor', days: 28 },
    { name: 'Eyre', days: 28 },
    { name: 'Dravago', days: 28 },
    { name: 'Nymm', days: 28 },
    { name: 'Lharvion', days: 28 },
    { name: 'Barrakas', days: 28 },
    { name: 'Rhaan', days: 28 },
    { name: 'Sypheros', days: 28 },
    { name: 'Aryth', days: 28 },
    { name: 'Vult', days: 28 },
  ],
  daysPerWeek: 7,
  weekDayNames: ['Sul', 'Mol', 'Zol', 'Wir', 'Zor', 'Far', 'Sar'],
  yearLabel: 'YK',
  epoch: 'Year of the Kingdom',
};

/** Gregorian Calendar — real-world reference / modern settings */
export const GREGORIAN_CALENDAR: CampaignCalendar = {
  name: 'Gregorian',
  months: [
    { name: 'January', days: 31 },
    { name: 'February', days: 28 },
    { name: 'March', days: 31 },
    { name: 'April', days: 30 },
    { name: 'May', days: 31 },
    { name: 'June', days: 30 },
    { name: 'July', days: 31 },
    { name: 'August', days: 31 },
    { name: 'September', days: 30 },
    { name: 'October', days: 31 },
    { name: 'November', days: 30 },
    { name: 'December', days: 31 },
  ],
  daysPerWeek: 7,
  weekDayNames: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  yearLabel: 'AD',
  epoch: 'Anno Domini',
};

export const CALENDAR_PRESETS = [
  HARPTOS_CALENDAR,
  GALIFAR_CALENDAR,
  GREGORIAN_CALENDAR,
] as const;

/** The Russian text of each preset: names, months and week days by position, the era labels. */
const RU: Record<string, { name: string; months: string[]; weekDays: string[]; yearLabel: string; epoch: string }> = {
  'Harptos (Forgotten Realms)': {
    name: 'Харптос (Забытые королевства)',
    months: ['Хаммер', 'Середина зимы', 'Альтурьяк', 'Чес', 'Тарсакх', 'Праздник зелени', 'Миртул', 'Киторн', 'Флеймрул', 'Середина лета', 'Элизиас', 'Элейнт', 'Праздник урожая', 'Марпенот', 'Уктар', 'Праздник луны', 'Найтал'],
    weekDays: ['Первый день', 'Второй день', 'Третий день', 'Четвёртый день', 'Пятый день', 'Шестой день', 'Седьмой день', 'Восьмой день', 'Девятый день', 'Десятый день'],
    yearLabel: 'DR',
    epoch: 'Летоисчисление долин',
  },
  'Galifar (Eberron)': {
    name: 'Галифар (Эберрон)',
    months: ['Зарантир', 'Оларун', 'Терендор', 'Эйр', 'Драваго', 'Ним', 'Харвион', 'Барракас', 'Раан', 'Сиферос', 'Арит', 'Вульт'],
    weekDays: ['Сул', 'Мол', 'Зол', 'Вир', 'Зор', 'Фар', 'Сар'],
    yearLabel: 'ГК',
    epoch: 'Год королевства',
  },
  Gregorian: {
    name: 'Григорианский',
    months: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
    weekDays: ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'],
    yearLabel: 'н. э.',
    epoch: 'Наша эра',
  },
};

/** A preset in the language of the UI: what the GM picks is saved as it is shown (month names are the calendar's own text). */
export function localizeCalendar(calendar: CampaignCalendar, locale: string): CampaignCalendar {
  const ru = locale === 'ru' ? RU[calendar.name] : undefined;
  if (ru === undefined || ru.months.length !== calendar.months.length) return calendar;
  return {
    ...calendar,
    name: ru.name,
    months: calendar.months.map((m, i) => ({ ...m, name: ru.months[i] })),
    weekDayNames: calendar.weekDayNames !== undefined && calendar.weekDayNames.length === ru.weekDays.length ? ru.weekDays : calendar.weekDayNames,
    yearLabel: ru.yearLabel,
    epoch: ru.epoch,
  };
}
